import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
// The LIBRARY route type — `routerStore.routesList` is the library
// RouterStore's compiled table, so this view reads the library's shape (HQ's
// local `types/RouteType` is the AUTHORING shape and is not assignable in this
// direction: its `DialogType` requires a `title` the library's does not).
import type { RouteType } from "@intrasync/stores";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import { resolveTier, type TierUserLike, type UserTier } from "auth/tier";
import {
  isDenyRuleForApp,
  routePermissionPath,
  type PageAccessRule,
} from "shared/permissionPath";
import { isSessionEnded } from "stores/sessionGuard";

export type { PageAccessRule };

/** One user row as the Permissions view reads it (builtin userStore cache). */
export interface PermissionsUser extends TierUserLike {
  id: number | string;
  email?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
}

/** One manageable page derived from the route definitions. */
export interface ManagedPage {
  name: string;
  title: string;
  path: string;
  group: string;
}

/**
 * What a tier actually buys server-side. Rendered next to the selected user so
 * an admin can see the REAL boundary while setting the cosmetic one. Kept in
 * sync with `auth/tier.ts` (which mirrors the API's `_shared/rbac-tier.ts`).
 */
const TIER_SUMMARY: Record<UserTier, string> = {
  viewer: "read-only — may list and view, but no mutating API calls",
  operator:
    "may run routine operational actions over the API (start/stop/restart, deploy, rollback, devops writes)",
  admin: "full control — everything operator can do, plus exec and host/secret configuration",
};

/**
 * Route ViewStore for Administration → Page Visibility: per-user page hiding.
 *
 * Model: default-visible with explicit deny rows. Hiding a page for a user
 * creates a `user-permissions` row (`isDenied: true`, `location` = the page's
 * permission path); unhiding removes it. `PageAccessStore` +
 * `pageAccessRouteGuard` read those rows IN THE BROWSER and bounce the user to
 * `Unauthorized?reason=page_blocked`. Admin-tier users are never hidden from —
 * the guard bypasses them, and this view disables their toggles.
 *
 * THE BOUNDARY — this is UX, not security:
 *
 *   Page visibility hides pages in this app's UI. It is NOT a security
 *   boundary: what an account may read or change through the API is governed
 *   by its role/tier, which the server enforces on every request.
 *
 * Deny rows are keyed by WEB ROUTE PATH (`layout + path`). No route→service
 * mapping exists and none is being invented, so no API hook consults them —
 * pinned by `packages/api/src/services/user/permissions/
 * page-visibility-boundary.test.ts`. That is why this view renders the
 * selected user's effective TIER (`selectedUserTier`) beside the switches:
 * the tier is the boundary an admin is actually looking for.
 *
 * Rows live in the `user-permissions` ServiceStore cache and users in the
 * builtin `userStore`; per the MobX rule this store only derives over them.
 */
export class PermissionsStore {
  rootStore: RootStore;

  selectedUserId: number | string | null = null;
  userFilter = "";
  loading = false;
  /** Path currently being toggled (disables that row's switch). */
  savingPath: string | null = null;
  /** A failed switch toggle (the API's message). */
  error: string | null = null;
  /**
   * Why the team / rules could not be loaded, or null after a successful
   * load. Kept apart from `error` so a failed toggle never replaces the list
   * and a failed load never reads as "No team members found." — the
   * ServiceStore swallows a failed `find`, so `initialize` asks for the
   * failure explicitly (`findOrThrow`). This is the ADMIN screen only; the
   * fail-open PageAccessStore / pageAccessRouteGuard seams are untouched.
   */
  loadError: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ---- Service seams ----

  private get usersService(): ServiceStoreType | null {
    return (
      (this.rootStore.userStore as unknown as ServiceStoreType | null) ?? null
    );
  }

  /**
   * The built-in `user-permissions` ServiceStore the RootStore mounts itself
   * (`userPermissionsStore` is a reserved key, so HQ does not configure a
   * second store against that path). Same seam as PageAccessStore.
   */
  private get permissionsService(): ServiceStoreType | null {
    return (
      (this.rootStore.userPermissionsStore as unknown as ServiceStoreType | null) ??
      null
    );
  }

  // ---- Loading (called from the route's beforeEnter) ----

  async initialize(): Promise<void> {
    this.loading = true;
    this.error = null;
    this.loadError = null;
    try {
      await Promise.all([
        findOrThrow(this.usersService, {
          query: { $limit: 1000, $sort: { email: 1 } },
        }),
        // Admin reads are unscoped, so this loads every user's rules.
        findOrThrow(this.permissionsService, { query: { $limit: 5000 } }),
      ]);
    } catch (err: unknown) {
      const message = loadErrorMessage(err, "Failed to load page visibility");
      runInAction(() => {
        if (!isSessionEnded(err)) this.loadError = message;
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  // ---- Users ----

  get users(): PermissionsUser[] {
    const entities = (this.usersService?.entities ??
      []) as unknown as PermissionsUser[];
    const filter = this.userFilter.trim().toLowerCase();
    return entities
      .filter((user) => user?.id != null)
      .filter((user) => {
        if (!filter) return true;
        const haystack = [user.email, user.firstName, user.lastName, user.role]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(filter);
      })
      .slice()
      .sort((a, b) => String(a.email || "").localeCompare(String(b.email || "")));
  }

  setUserFilter(value: string) {
    this.userFilter = value;
  }

  selectUser(userId: number | string | null) {
    this.selectedUserId = userId;
    this.error = null;
  }

  get selectedUser(): PermissionsUser | null {
    if (this.selectedUserId == null) return null;
    return (
      this.users.find(
        (user) => String(user.id) === String(this.selectedUserId)
      ) ?? null
    );
  }

  /** Admin-tier users are never hidden from — their pages can't be hidden. */
  get selectedUserIsAdmin(): boolean {
    return this.selectedUserTier === "admin";
  }

  /**
   * The selected user's effective RBAC tier — the boundary the SERVER enforces,
   * as opposed to the page hiding this view authors. Null when no user is
   * selected. Resolved through the one web-side tier helper, which reads the
   * API-computed `tier` and never re-derives role→tier itself.
   */
  get selectedUserTier(): UserTier | null {
    return resolveTier(this.selectedUser);
  }

  /** Plain-language description of what {@link selectedUserTier} allows. */
  get selectedUserTierSummary(): string {
    const tier = this.selectedUserTier;
    return tier ? TIER_SUMMARY[tier] : "";
  }

  userDisplayName(user: PermissionsUser): string {
    const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
    return name || String(user.email || user.id);
  }

  // ---- Pages ----

  /**
   * The manageable pages, derived from the route definitions: real, visible,
   * authenticated app pages. Admin-only routes are excluded — non-admins never
   * see them and admins can't be hidden from, so a rule would be inert.
   */
  get pages(): ManagedPage[] {
    const routesList: RouteType[] =
      this.rootStore.routerStore?.routesList ?? [];
    const seen = new Set<string>();
    const pages: ManagedPage[] = [];
    for (const route of routesList) {
      if (!route?.name || !route.path) continue;
      if (route.hidden) continue;
      if (route.requiresAuth === false) continue;
      if (route.requiresAdmin) continue;
      if (route.parentRoute || route.forwardRoute || route.projectRoute)
        continue;
      if (route.path.includes("/auth/") || route.path.includes("/:")) continue;
      // MUST be the same derivation the browser-side gate uses
      // (pageAccessRouteGuard via routePermissionPath) or deny rows never match.
      const path = routePermissionPath(route);
      if (seen.has(path)) continue;
      seen.add(path);
      pages.push({
        name: route.name,
        title:
          route.title || route.viewportCard?.header?.title || route.name,
        path,
        group: route.group || "Other",
      });
    }
    return pages.sort((a, b) => {
      const groupCompare = a.group.localeCompare(b.group);
      if (groupCompare !== 0) return groupCompare;
      return a.title.localeCompare(b.title);
    });
  }

  /** Pages grouped for rendering: [group, pages[]] preserving page order. */
  get pagesByGroup(): Array<[string, ManagedPage[]]> {
    const groups = new Map<string, ManagedPage[]>();
    for (const page of this.pages) {
      const list = groups.get(page.group) ?? [];
      list.push(page);
      groups.set(page.group, list);
    }
    return Array.from(groups.entries());
  }

  // ---- Deny rules ----

  private get allRules(): PageAccessRule[] {
    return (this.permissionsService?.entities ??
      []) as unknown as PageAccessRule[];
  }

  /**
   * All deny rules in this app grouped per user and keyed by location — one
   * computed pass over the rule cache, so the per-row reads below (user-list
   * badges, page switches) are map lookups instead of rescans on each render.
   */
  private get denyRulesByUser(): Map<string, Map<string, PageAccessRule>> {
    const appName = this.rootStore.appName;
    const byUser = new Map<string, Map<string, PageAccessRule>>();
    for (const rule of this.allRules) {
      if (!rule?.location) continue;
      if (!isDenyRuleForApp(rule, appName)) continue;
      const key = String(rule.userId);
      const forUser = byUser.get(key) ?? new Map<string, PageAccessRule>();
      forUser.set(String(rule.location), rule);
      byUser.set(key, forUser);
    }
    return byUser;
  }

  private get selectedDenyRules(): Map<string, PageAccessRule> {
    if (this.selectedUserId == null) return new Map();
    return (
      this.denyRulesByUser.get(String(this.selectedUserId)) ?? new Map()
    );
  }

  /** How many pages are hidden for a user (badge in the user list). */
  hiddenCountFor(userId: number | string): number {
    return this.denyRulesByUser.get(String(userId))?.size ?? 0;
  }

  /** Whether this page is hidden in the UI for the selected user. */
  isPageHidden(page: ManagedPage): boolean {
    return this.selectedDenyRules.has(page.path);
  }

  /** Show or hide one page for the selected user. */
  async togglePage(page: ManagedPage): Promise<void> {
    const userId = this.selectedUserId;
    const service = this.permissionsService;
    if (userId == null || !service || this.selectedUserIsAdmin) return;
    this.savingPath = page.path;
    this.error = null;
    try {
      const existing = this.selectedDenyRules.get(page.path);
      if (existing) {
        await service.remove(existing.id);
      } else {
        await service.create({
          userId,
          appName: this.rootStore.appName,
          location: page.path,
          createAllowed: false,
          findAllowed: false,
          patchAllowed: false,
          removeAllowed: false,
          isDenied: true,
        });
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to update page visibility";
      runInAction(() => {
        if (!isSessionEnded(err)) this.error = message;
      });
    } finally {
      runInAction(() => {
        this.savingPath = null;
      });
    }
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.selectedUserId = null;
    this.userFilter = "";
    this.loading = false;
    this.savingPath = null;
    this.error = null;
    this.loadError = null;
  }
}

export default PermissionsStore;
